import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';

interface AuditEntry {
  id: string;
  action: string;
  actorId: string;
  actor: {
    displayName: string;
    handle: string;
  };
  targetType: 'user' | 'post' | 'comment' | 'report' | 'ad' | 'settings';
  targetId?: string;
  details?: Record<string, any>;
  ipAddress?: string;
  createdAt: string;
}

export default function AdminAuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  
  useEffect(() => {
    const loadAuditLog = async () => {
      setLoading(true);
      try {
        const res = await api.admin.getAuditLog({
          action: filter === 'all' ? undefined : filter,
          search: search || undefined,
        });
        setEntries(res);
      } catch (err) {
        console.error('Failed to load audit log:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadAuditLog();
  }, [filter, search]);
  
  const getActionColor = (action: string) => {
    if (action.includes('ban') || action.includes('delete') || action.includes('remove')) {
      return 'text-red-600 dark:text-red-400';
    }
    if (action.includes('warn') || action.includes('suspend')) {
      return 'text-yellow-600 dark:text-yellow-400';
    }
    if (action.includes('create') || action.includes('approve')) {
      return 'text-green-600 dark:text-green-400';
    }
    return 'text-slate-600 dark:text-slate-400';
  };
  
  const formatAction = (action: string) => {
    return action
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  const actionTypes = [
    'all',
    'user_ban',
    'user_unban',
    'user_warn',
    'user_suspend',
    'post_remove',
    'post_approve',
    'report_resolve',
    'report_dismiss',
    'ad_create',
    'ad_update',
    'ad_delete',
    'settings_update',
  ];
  
  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Audit Log</h1>
          <p className="text-slate-500 dark:text-slate-400">Track all admin actions</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by user..."
            className="input w-full sm:w-64"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input"
          >
            {actionTypes.map((type) => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Actions' : formatAction(type)}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="card overflow-hidden">
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto" />
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="card p-12 text-center">
          <LogIcon className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">No entries found</h2>
          <p className="text-slate-500 dark:text-slate-400">
            {filter !== 'all' ? 'Try changing the filter' : 'Admin actions will appear here'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Admin</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Action</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Target</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300">Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {formatDateTime(new Date(entry.createdAt))}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="avatar avatar-sm">
                          {(entry.actor?.displayName || 'A').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">
                            {entry.actor?.displayName || 'Admin'}
                          </p>
                          <p className="text-xs text-slate-500">@{entry.actor?.handle || 'admin'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${getActionColor(entry.action)}`}>
                        {formatAction(entry.action)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium text-slate-600 dark:text-slate-300 capitalize">
                        {entry.targetType}
                      </span>
                      {entry.targetId && (
                        <span className="ml-2 text-xs text-slate-400">#{entry.targetId.slice(0, 8)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 max-w-xs truncate">
                      {entry.details ? (
                        <button
                          onClick={() => alert(JSON.stringify(entry.details, null, 2))}
                          className="text-primary-500 hover:text-primary-600"
                        >
                          View details
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination placeholder */}
          <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Showing {entries.length} entries
            </p>
            <div className="flex gap-2">
              <button className="btn btn-secondary" disabled>Previous</button>
              <button className="btn btn-secondary" disabled>Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}
